/**
 * KI-Verbrauch pro Mandant/Nutzer (WP2b) — konsumiert `GET /api/ki-verbrauch`
 * (routes/ki-verbrauch.ts). Punkt 124 (Antoine, 26.09.2026, Entscheidung 5): von Business Health
 * in die Einstellungen (Tab „Module“, neben dem System-Status) umgezogen — KI-Kosten gehören nicht
 * in die Geschäftszahlen. Inhalt unverändert.
 */
import { useEffect, useState } from "react";
import { apiFetch, toApiError } from "../lib/api";
import { money } from "./format";
import { EmptyState, ErrorNote, Icon, KpiCard, LoadingRows } from "./components";
import type { KiVerbrauchResponse, KiVerbrauchZeitraum } from "./types";

const KI_VERBRAUCH_ZEITRAUM_LABEL: Record<KiVerbrauchZeitraum, string> = {
  heute: "Heute",
  monat: "Diesen Monat",
};

export function KiVerbrauchKarte() {
  const [zeitraum, setZeitraum] = useState<KiVerbrauchZeitraum>("monat");
  const [daten, setDaten] = useState<KiVerbrauchResponse | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLaedt(true);
    setFehler(null);
    (async () => {
      try {
        const res = await apiFetch(`/api/ki-verbrauch?zeitraum=${zeitraum}`);
        if (!res.ok) throw await toApiError(res);
        const body = (await res.json()) as { data: KiVerbrauchResponse };
        if (alive) setDaten(body.data);
      } catch (e) {
        if (alive) setFehler(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLaedt(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [zeitraum, reloadTick]);

  return (
    <section
      className="mb-6 rounded-2xl border border-[#F0E7D6] p-5 dark:border-white/[0.06]"
      data-testid="ki-verbrauch-section"
    >
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon name="smart_toy" className="text-[18px] text-primary" />
          <h2 className="font-title-md text-title-md font-semibold text-on-background dark:text-inverse-on-surface">
            KI-Verbrauch
          </h2>
        </div>
        <div className="inline-flex gap-1 rounded-lg bg-surface-container-high p-1 dark:bg-white/5" role="group" aria-label="Zeitraum">
          {(Object.keys(KI_VERBRAUCH_ZEITRAUM_LABEL) as KiVerbrauchZeitraum[]).map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => setZeitraum(z)}
              aria-pressed={zeitraum === z}
              className={`rounded-md px-3 py-1.5 font-body-sm text-body-sm font-medium transition-colors ${
                zeitraum === z
                  ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                  : "text-on-surface-variant hover:text-on-background dark:text-surface-variant dark:hover:text-inverse-on-surface"
              }`}
            >
              {KI_VERBRAUCH_ZEITRAUM_LABEL[z]}
            </button>
          ))}
        </div>
      </div>

      {laedt && <LoadingRows rows={3} />}

      {!laedt && fehler && (
        <ErrorNote message={fehler} title="KI-Verbrauch konnte nicht geladen werden" onRetry={() => setReloadTick((t) => t + 1)} />
      )}

      {!laedt && !fehler && daten && daten.gesamt.aktionen === 0 && (
        <EmptyState
          icon="smart_toy"
          title="Noch kein KI-Verbrauch im Zeitraum"
          hint="Sobald KI-Funktionen genutzt werden (z. B. die KI-Hinweise auf der Auswertung), erscheinen hier Anzahl und geschätzte Kosten."
        />
      )}

      {!laedt && !fehler && daten && daten.gesamt.aktionen > 0 && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
            {/* Betrag/Zahl sind formatierungsabhängig -> eigener testid statt Text-Matching
                (README.md "Selektoren", Vorbild `builder-gesamtsumme`). */}
            <div data-testid="ki-verbrauch-kachel-aktionen">
              <KpiCard label="KI-Aktionen" value={daten.gesamt.aktionen} icon="bolt" />
            </div>
            <div data-testid="ki-verbrauch-kachel-kosten">
              <KpiCard label="Geschätzte Kosten" value={money(daten.gesamt.geschaetzteKostenCent)} icon="payments" iconTone="info" />
            </div>
          </div>
          {daten.gesamt.aktionenOhneMessung > 0 && (
            <p className="mt-2 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
              davon {daten.gesamt.aktionenOhneMessung} ohne Token-Messung
            </p>
          )}

          <div className="mt-5">
            <h3 className="font-body-md text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
              Pro Feature
            </h3>
            <ul className="mt-2 divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
              {daten.proFeature.map((f) => (
                <li key={f.feature} className="flex items-center justify-between gap-3 py-2">
                  <span className="font-body-sm text-[13px] text-on-background dark:text-inverse-on-surface">{f.feature}</span>
                  <span className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                    {f.aktionen} Aktionen · {money(f.geschaetzteKostenCent)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* proNutzer fehlt bei Staff (Rollen-Gate der Route) — dann bleibt diese Sektion aus. */}
          {daten.proNutzer && (
            <div className="mt-5" data-testid="ki-verbrauch-pro-nutzer">
              <h3 className="font-body-md text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                Pro Nutzer
              </h3>
              <ul className="mt-2 divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
                {daten.proNutzer.map((n) => (
                  <li key={n.userId ?? "system"} className="flex items-center justify-between gap-3 py-2">
                    <span className="font-body-sm text-[13px] text-on-background dark:text-inverse-on-surface">
                      {n.userId ?? "System / automatisch"}
                    </span>
                    <span className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                      {n.aktionen} Aktionen · {money(n.geschaetzteKostenCent)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-4 font-body-sm text-[11px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
            Geschätzt, keine Abrechnung: {daten.preisstand}
          </p>
        </>
      )}
    </section>
  );
}
